import React, { useEffect } from "react";
import { Check, ChevronRight, MapPin, Briefcase, Banknote, ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { BewerbungForm } from "@/components/site/BewerbungForm";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import jobs from "@/data/jobs.json";

const Liste = ({ h2, items, testId }) => (
  <div data-testid={testId}>
    <h2 className="mt-12 text-[22px] font-bold leading-[1.3] text-[#1e2532] md:text-[25px]">{h2}</h2>
    <ul className="mt-5 space-y-3.5">
      {items.map((it) => (
        <li key={it} className="flex gap-3 text-[16.5px] leading-[1.7] text-[#3c4356]">
          <span className="mt-[5px] flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
            <Check size={11} strokeWidth={3} className="text-white" />
          </span>
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function JobDetail({ slug }) {
  const s = jobs.stellen[slug];
  useEffect(() => {
    document.title = s.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", s.metaDescription);
    window.scrollTo(0, 0);
  }, [s]);

  return (
    <>
      <Header />
      <main data-testid={`job-main-${slug}`}>
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container className="max-w-[820px]">
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              <a href="/jobs/" className="hover:text-white">Jobs</a>
              <ChevronRight size={13} />
              <span className="text-white/80">{s.rolle}</span>
            </nav>
            <Label>{s.rolle}</Label>
            <h1 className="mt-7 font-bold" data-testid="job-h1" style={{ fontSize: "clamp(27px,3.6vw,42px)", lineHeight: 1.18 }}>
              {s.title}
            </h1>
            <p className="mt-5 text-[15.5px] text-white/55">{s.unternehmen}</p>
            <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3 text-[15px] text-white/80">
              <span className="inline-flex items-center gap-2">
                <MapPin size={16} className="text-[#3ecf9b]" /> {s.ort}
              </span>
              <span className="inline-flex items-center gap-2">
                <Briefcase size={16} className="text-[#3ecf9b]" /> {s.art}
              </span>
              {s.gehalt && (
                <span className="inline-flex items-center gap-2">
                  <Banknote size={16} className="text-[#3ecf9b]" /> {s.gehalt}
                </span>
              )}
            </div>
            <a
              href="#bewerbung"
              data-testid="job-cta"
              className="btn-solid mt-9 inline-flex items-center px-7 py-[15px] text-[13.5px] font-bold uppercase tracking-[0.06em]"
            >
              <span>Jetzt bewerben</span>
            </a>
          </Container>
        </section>

        <Section testId="job-content" className="bg-white">
          <Container className="max-w-[820px]">
            <p className="text-[17px] leading-[1.85] text-[#3c4356]">{s.intro}</p>
            <Liste h2="Deine Aufgaben" items={s.aufgaben} testId="job-aufgaben" />
            <Liste h2="Dein Profil" items={s.profil} testId="job-profil" />
            <Liste h2="Das wird dir geboten" items={s.bieten} testId="job-bieten" />
            <p className="mt-12">
              <a href="/jobs/" className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                <span className="link-underline">Alle offenen Stellen</span>
                <ArrowRight size={15} />
              </a>
            </p>
          </Container>
        </Section>

        <BewerbungForm
          defaultRolle={s.rolle}
          stelle={s.title}
          h2="Auf diese Stelle bewerben"
          intro="Fülle einfach das Formular aus — Lebenslauf brauchst du dafür nicht. Wir melden uns innerhalb von 24 Stunden und besprechen alles Weitere telefonisch."
        />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
